import apiClient from './apiClient';
import { 
  FirmBasic, 
  FirmDetailed, 
  FirmUpdate, 
  UserDetailed, 
  Country 
} from '../types/firm';

/**
 * Firm API functions with enhanced error handling and typing
 */
export const firmApi = {
  /**
   * Get all firms with optional user inclusion
   */
  getAllFirms: async (includeUsers: boolean = false): Promise<FirmDetailed[]> => {
    try {
      console.log('Fetching firms from API...');
      const url = includeUsers ? '/api/v2/web/firms?include_users=true' : '/api/v2/web/firms';
      const response = await apiClient.get<FirmDetailed[]>(url);
      console.log('API response data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getAllFirms:', error);
      throw error;
    }
  },
  
  /**
   * Get a single firm by ID with optional user inclusion
   */
  getFirmById: async (firmId: string, includeUsers: boolean = false): Promise<FirmDetailed> => {
    try {
      const url = includeUsers ? 
        `/api/v2/web/firms/${firmId}?include_users=true` : 
        `/api/v2/web/firms/${firmId}`;
      const response = await apiClient.get<FirmDetailed>(url);
      return response.data;
    } catch (error) {
      console.error('Error in getFirmById:', error);
      throw error;
    }
  },
  
  /**
   * Get users for a specific firm
   */
  getFirmUsers: async (firmId: string, skip: number = 0, limit: number = 100): Promise<UserDetailed[]> => {
    try {
      const url = `/api/v2/web/firms/${firmId}/users?skip=${skip}&limit=${limit}`;
      const response = await apiClient.get<UserDetailed[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error in getFirmUsers:', error);
      throw error;
    }
  },
  
  /**
   * Create a new firm
   */
  createFirm: async (firmData: FirmUpdate): Promise<FirmBasic> => {
    try {
      const dataToSend: FirmUpdate = {
        name: firmData.name,
        abbrev: firmData.abbrev,
        Country_ID: firmData.Country_ID || null,
        logo: firmData.logo || null,
        url: firmData.url || null,
        primary_color: firmData.primary_color || null,
        secondary_color: firmData.secondary_color || null,
        domain: firmData.domain || null,
        status: firmData.status || 1
      };
      
      console.log('firmApi.createFirm - Sanitized request data:', dataToSend);
      const response = await apiClient.post<FirmBasic>('/api/v2/web/firms', dataToSend);
      console.log('firmApi.createFirm - Response data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in createFirm:', error);
      throw error;
    }
  },
  
  /**
   * Update an existing firm
   */
  updateFirm: async (firmId: string, firmData: FirmUpdate): Promise<FirmBasic> => {
    try {
      const dataToSend: FirmUpdate = {
        name: firmData.name,
        abbrev: firmData.abbrev,
        Country_ID: firmData.Country_ID || null,
        logo: firmData.logo || null,
        url: firmData.url || null,
        primary_color: firmData.primary_color || null,
        secondary_color: firmData.secondary_color || null,
        domain: firmData.domain || null,
        status: firmData.status || 1
      };
      
      console.log('firmApi.updateFirm - Sanitized request data:', dataToSend);
      const response = await apiClient.put<FirmBasic>(`/api/v2/web/firms/${firmId}`, dataToSend);
      console.log('firmApi.updateFirm - Response data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in updateFirm:', error);
      throw error;
    }
  },
  
  /**
   * Delete a firm
   */
  deleteFirm: async (firmId: string): Promise<{ message: string }> => {
    try {
      const response = await apiClient.delete<{ message: string }>(`/api/v2/web/firms/${firmId}`);
      return response.data;
    } catch (error) {
      console.error('Error in deleteFirm:', error);
      throw error;
    }
  },

  /**
   * Get firms lookup data (minimal data for dropdowns/selection)
   */
  getFirmsLookup: async (): Promise<{Firm_ID: string, name: string, abbrev: string, status: number}[]> => {
    try {
      const response = await apiClient.get<{Firm_ID: string, name: string, abbrev: string, status: number}[]>('/api/v2/web/firms/lookup/firms');
      return response.data;
    } catch (error) {
      console.error('Error in getFirmsLookup:', error);
      throw error;
    }
  },

  /**
   * Get countries lookup data specifically for firm management
   */
  getCountriesLookupForFirms: async (): Promise<Country[]> => {
    try {
      const response = await apiClient.get<Country[]>('/api/v2/web/firms/lookup/countries-for-firms');
      return response.data;
    } catch (error) {
      console.error('Error in getCountriesLookupForFirms:', error);
      throw error;
    }
  },

  /**
   * Get users lookup data specifically for firm management
   */
  getUsersLookupForFirms: async (): Promise<{User_ID: string, firstname: string, lastname: string, mail: string, Firm_ID: string, firm_name: string, verification: number}[]> => {
    try {
      const response = await apiClient.get<{User_ID: string, firstname: string, lastname: string, mail: string, Firm_ID: string, firm_name: string, verification: number}[]>('/api/v2/web/firms/lookup/users-for-firms');
      return response.data;
    } catch (error) {
      console.error('Error in getUsersLookupForFirms:', error);
      throw error;
    }
  },

  /**
   * Get all countries lookup data (legacy function - kept for backward compatibility)
   * @deprecated Use getCountriesLookup() or getCountriesLookupForFirms() instead
   */
  getAllCountries: async (): Promise<Country[]> => {
    try {
      const response = await apiClient.get<Country[]>('/api/v2/web/firms/lookup/countries');
      return response.data;
    } catch (error) {
      console.error('Error in getAllCountries:', error);
      throw error;
    }
  }
};
